import React, { useEffect, useState, useRef } from 'react'
import cx from 'classnames'
import { func, string } from 'prop-types'
import { DotsIcon } from '@wf-sl/common-icons'
import { Button } from '@wf-common/atoms'

import List from '../list/list'
import Tooltip from '../tooltip/tooltip'
import { placementPropType } from '../tooltip/tooltip-prop-type'
import { dropdownPlacementTypes, dropdownPlacementTypesPropType } from './constants'
import { WidthObservedElement } from './width-observed-element'

import styles from './more-button.module.css'

const propTypes = {
    dropdownPlacement: dropdownPlacementTypesPropType,
    moreButtonTooltip: string,
    moreButtonTooltipPlacement: placementPropType,
    onWidthChanged: func
}

const defaultProps = {
    dropdownPlacement: dropdownPlacementTypes.TOP_RIGHT,
    onWidthChanged: () => {}
}

/**
 * Render the more button with dropdown list of buttons not visible (not fitting) in the button bar.
 *
 * @param {ReactElement} [children] - The buttons to be rendered in dropdown list
 * @param {string} [dropdownPlacement] - Optional position of the more button dropdown - default is top-right
 * @param {string} [moreButtonTooltip] - content of the more button tooltip
 * @param {string} [moreButtonTooltipPlacement] - position of the more button tooltip
 * @param {function} [onWidthChanged] - Callback when width of button has changed
 * @returns {ReactElement}
 */
export function MoreButton({
    children,
    dropdownPlacement,
    moreButtonTooltip,
    moreButtonTooltipPlacement,
    onWidthChanged
}) {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const moreButtonRef = useRef()

    useEffect(() => {
        if (isDropdownOpen) {
            const onDocumentClick = clickEvent => {
                if (isDropdownOpen && moreButtonRef.current && !moreButtonRef.current.contains(clickEvent.target)) {
                    setIsDropdownOpen(false)
                }
            }
            document.addEventListener('click', onDocumentClick)

            return () => {
                document.removeEventListener('click', onDocumentClick)
            }
        }
    }, [isDropdownOpen])

    const dropdownClassName = cx(styles['dropdown'], {
        [styles['hidden']]: !isDropdownOpen,
        [styles[dropdownPlacement]]: dropdownPlacement
    })

    const moreButton = (
        <WidthObservedElement onWidthChanged={onWidthChanged}>
            <div ref={moreButtonRef} className={cx(styles['more-button-wrapper'])}>
                <Button className="t3sel-more-button" icon onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                    <DotsIcon size={14} />
                </Button>
                {isDropdownOpen && (
                    <div className={dropdownClassName}>
                        <List className={cx('t3sel-hidden-buttons', styles['list'])}>{children}</List>
                    </div>
                )}
            </div>
        </WidthObservedElement>
    )

    if (moreButton && moreButtonTooltip) {
        const tooltipProps = {
            content: <span>{moreButtonTooltip}</span>,
            placement: moreButtonTooltipPlacement
        }

        // The tooltip should be invisible if the dropdown is showing up to avoid overlapping of both.
        // As the tooltip component does not keep the original visibility behaviour of the rc-tooltip
        // we have to use the manualTrigger property to force invisibility here.
        if (isDropdownOpen) {
            tooltipProps.manualTrigger = true
        }
        return <Tooltip {...tooltipProps}>{moreButton}</Tooltip>
    } else {
        return moreButton
    }
}

MoreButton.defaultProps = defaultProps
MoreButton.propTypes = propTypes
